import React from 'react';
import {
  Typography,
  Stack,
  Divider,
  Card,
  CardContent,
  CardActions,
} from '@mui/material';
import PropTypes from 'prop-types';
import ActuatorControl from '../../../ActuatorControl';
import { featurePropTypes } from '../../types';
import ActuatorFeatureState from '../../../ActuatorFeatureState';
import { Zigbee2MQTTUtils } from '../../../../utils/zigbeeUtils';
import { linkqualityFilter } from '../../utils/filters';
import { StatusSummary } from '../StatusSummary';
import { useActuatorContext } from '../../../../context/ActuatorContext';
import ConfirmingDisplay from '../ConfirmingDisplay';
import ActuatorIconWithStatus from '../../../ActuatorIconWithStatus';

const CardControlPanel = ({
  gateway,
  deviceId,
  deviceName,
  icon,
  features,
  readOnly,
}) => {
  const { state } = useActuatorContext();
  const mainFeatures = features.filter(f => !linkqualityFilter(f));

  return (
    <Stack gap={1}>
      <Stack
        direction="row"
        gap={1}
        alignItems="center"
        justifyContent="center"
      >
        <ActuatorIconWithStatus
          type="library"
          name={icon}
          features={mainFeatures}
        />

        <Typography variant="h5" fontWeight="bold" textAlign="center">
          {deviceName}
        </Typography>

        <StatusSummary features={features} />
      </Stack>
      <Stack gap={3} direction="row" flexWrap="wrap" alignItems="stretch">
        {mainFeatures.map(feature => {
          const stateId = `${gateway}-${deviceId}-${feature.property}`;
          const { confirming = false } = state[stateId] ?? {};
          return (
            <Card
              key={feature.property}
              sx={{
                height: 'auto',
                flex: '1 0 30%',
                minWidth: 'min-content',
                filter: readOnly && 'grayscale(0.5)',
                opacity: readOnly && '0.8',
              }}
            >
              <CardContent>
                <Stack>
                  <Stack
                    direction="row"
                    alignItems="flex-start"
                    justifyContent="space-between"
                    width="100%"
                  >
                    <Typography gutterBottom>{feature.label}</Typography>
                    <ConfirmingDisplay
                      confirming={confirming}
                      lastKnownDate={feature.reading?.date}
                      hasState={Zigbee2MQTTUtils.hasState(feature.access)}
                    />
                  </Stack>
                  {feature.description && (
                    <Typography variant="body2" color="lightgray" gutterBottom>
                      {feature.description}
                    </Typography>
                  )}
                </Stack>
                <Divider
                  sx={{
                    borderColor: theme => theme.palette.primary.main,
                    borderWidth: '1px',
                  }}
                />
              </CardContent>
              <CardActions
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  padding: '0 2rem 1rem 2rem',
                }}
              >
                {Zigbee2MQTTUtils.hasSet(feature.access) ? (
                  <ActuatorControl
                    gateway={gateway}
                    deviceId={deviceId}
                    deviceName={deviceName}
                    feature={feature}
                    readOnly={readOnly}
                  />
                ) : (
                  <ActuatorFeatureState reading={feature.reading} />
                )}
              </CardActions>
            </Card>
          );
        })}
      </Stack>
    </Stack>
  );
};

CardControlPanel.propTypes = {
  readOnly: PropTypes.bool,
  gateway: PropTypes.string,
  deviceId: PropTypes.string,
  deviceName: PropTypes.string,
  icon: PropTypes.string,
  features: PropTypes.arrayOf(featurePropTypes),
};

export default CardControlPanel;
